import { Component, Inject, Input, OnDestroy, OnInit, OnChanges, SimpleChanges} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DATA_SOURCE, IDataSource } from '@ep-om/project/data-sources/datasource.abstract';
import { FormlyFormOptions } from '@ngx-formly/core';
import { FormlyHookConfig } from '@ngx-formly/core/lib/models';
import clone from 'clone';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { IFormlyField, TFieldGroupExtended } from '../formly-editor/formly-editor.component';
import { OnInitHooks } from './hooks/hooks';
import { MetaIntyerfacePemissionType } from '@ep-om/project/components/interfaces/_meta-interface/meta-interface.component';

@Component({
  selector: 'formly-viewer',
  templateUrl: './formly-viewer.component.html',
  styleUrls: ['./formly-viewer.component.scss']
})
export class FormlyViewerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() fields$: Observable<IFormlyField[]>;
  @Input() editing: boolean = false;
  @Input() form?: FormGroup;
  @Input() model?: any;
  @Input() permission: MetaIntyerfacePemissionType;
  @Input() interfaceName: string;
  _permission$ = new BehaviorSubject<MetaIntyerfacePemissionType>(null);

  private _destroy$ = new Subject<void>();

  vm$: Observable<{ fields: IFormlyField[]; options: FormlyFormOptions; form: FormGroup; model: any }>;

  constructor(@Inject(DATA_SOURCE) private _dataSources: IDataSource[]) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.permission?.currentValue && changes.permission.currentValue !== changes.permission.previousValue) {
      this._permission$.next(changes.permission.currentValue);
    }
  }

  ngOnInit(): void {
    const updatedModel = {...this.model, interfaceName: this.interfaceName};

    this.vm$ = combineLatest([this.fields$, this._permission$]).pipe(
      filter(([fields, permission]) => !!fields),
      map(([groups, permission]) => {
        const _groups = clone(groups);
        return {
          fields: _groups.map((group) => {
            if ('template' in group) {
              return group;
            }
            group.fieldGroup = group.fieldGroup.map((field) => {
              field.props.options = this.getOptions(clone(field));
              field.props.disabled = permission !== 'editable';
              field.hooks = this.getHooks(clone(field), _groups);
              return field;
            });
            this._setDeactivableWrapper(group, permission === 'editable');
            return group;
          }),
          options: null,
          model: updatedModel || {},
          form: this.form || new FormGroup({})
        };
      })
    );
  } 

  private getHooks(field: TFieldGroupExtended, groups: IFormlyField[]): FormlyHookConfig {
    const hooks: FormlyHookConfig = {
      onInit: (field) => {
        Object.keys(OnInitHooks).forEach((hook) => OnInitHooks[hook](field, groups, this._destroy$));
      },
      onDestroy: () => {},
      afterContentInit: () => {},
      afterViewInit: () => {},
      onChanges: () => {}
    };
    return hooks;
  }

  private getOptions(field: TFieldGroupExtended): Observable<any[]> {
    if (!field.dataSource?.name) {
      if (field.props.options instanceof Observable) {
        return field.props.options;
      }
      return of(field.props.options);
    }
    const dataSource = field.dataSource;
    let options$: Observable<{ value: string; label: string; source: any }[]>;
    let args: any;
    if (dataSource.name === 'QUERY') {
      const { queryId, optionValue, optionLabel } = dataSource;
      args = { queryId, optionValue, optionLabel };
    }
    options$ = this._dataSources.find((ds) => ds.name === field.dataSource.name).getOptions$(args);

    return options$.pipe(
      map((options) => {
        if (Array.isArray(field.props.options)) {
          return options.concat(field.props.options);
        }
        return options;
      })
    );
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _setDeactivableWrapper(rowField: IFormlyField, isEditable: boolean): void {
    if (!rowField.props?.deactivable) return;
    rowField.wrappers = ['deactivable'];
    rowField.props.isEditable = isEditable;
    rowField.props.deactivable.label = 'Enable';
    /* NB_ sibling keys includes required toggle's key, which will be required inside the wrapper */
    rowField.props.deactivable.siblingKeys = (rowField?.fieldGroup || []).map(field => field?.key);
  } 
}
